import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Add, Edit, Delete, History } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import UserForm from '../components/users/UserForm';
import { User, UserRole } from '../types/user';
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} from '../services/userApi';
import { useNavigate } from 'react-router-dom';

export default function Users() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Get current user's role from Redux store
  const currentUserRole = useSelector((state: any) => state.user.role);
  const isSupport = currentUserRole === UserRole.SUPPORT;
  const currentUserIsAdmin = currentUserRole === UserRole.ADMIN;
  const currentUserAMC = useSelector((state: any) => state.user.amcId);

  // RTK Query hooks
  const { data: usersData, isLoading, isError, refetch } = useGetUsersQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const users = usersData?.users || [];

  const roleDisplayMap: { [key: string]: string } = {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.SUPPORT]: 'Support',
    [UserRole.AMC]: 'AMC User',
  };

  const handleEditUser = (user: User) => {
    setCurrentUser(user);
    setOpenForm(true);
  };

  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete).unwrap();
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success',
      });
      refetch();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete user',
        severity: 'error',
      });
    } finally {
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const handleFormSubmit = async (
    userData: Omit<User, 'id'> & { changePassword: boolean; amcId?: string }
  ) => {
    if (!currentUser && !userData.password) {
      setSnackbar({
        open: true,
        message: 'Password is required',
        severity: 'error',
      });
      return;
    }

    try {
      if (currentUser) {
        const { password, ...rest } = userData;
        await updateUser({
          id: currentUser.id,
          userData: userData.changePassword ? userData : rest,
        }).unwrap();
        setSnackbar({
          open: true,
          message: 'User updated successfully',
          severity: 'success',
        });
      } else {
        await createUser({ ...userData, ...(userData.amcId ? { amc: { id: userData.amcId } } : {}) }).unwrap();
        setSnackbar({
          open: true,
          message: 'User created successfully',
          severity: 'success',
        });
      }
      setOpenForm(false);
      refetch();
    } catch (error) {
      console.error('Failed to save user:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${currentUser ? 'update' : 'create'} user`,
        severity: 'error',
      });
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{ mb: isMobile ? 2 : 0, alignSelf: isMobile ? 'flex-start' : 'center' }}
        >
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/users/history')}
            sx={{ border: '1px solid', borderColor: 'divider' }}
            aria-label="User management history"
          >
            <History />
          </IconButton>
          {!isSupport && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => {
                setCurrentUser(null);
                setOpenForm(true);
              }}
              sx={{ width: isMobile ? 'auto' : 'auto' }}
            >
              Add User
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ width: '100%', mb: 2, position: 'relative' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 3, color: 'error.main' }}>Failed to load users. Please try again.</Box>
        ) : isMobile ? (
          <Box>
            {users.length === 0 ? (
              <Typography align="center" sx={{ py: 3 }}>
                No users found
              </Typography>
            ) : (
              users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <Paper key={user.id} sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <Box>
                      {!isSupport && (
                        <IconButton
                          onClick={() => handleEditUser(user)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => navigate(`/user/${user.id}/logs`)}
                        color="default"
                        size="small"
                        aria-label="User history"
                      >
                        <History />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(user.id)}
                        color="error"
                        size="small"
                        disabled={user.email === 'admin@kfintech.com'}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Role: {roleDisplayMap[user.role] || user.role}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: user.isActive ? 'success.main' : 'error.main',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2">
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{roleDisplayMap[user.role] || user.role}</TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: user.isActive ? 'success.main' : 'error.main',
                                mr: 1,
                              }}
                            />
                            {user.isActive ? 'Active' : 'Inactive'}
                          </TableCell>
                          <TableCell align="right">
                            {!isSupport && (
                              <IconButton onClick={() => handleEditUser(user)} color="primary">
                                <Edit />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={() => navigate(`/user/${user.id}/logs`)}
                              color="default"
                              aria-label="User history"
                            >
                              <History />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(user.id)}
                              color="error"
                              disabled={user.email === 'admin@kfintech.com'}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </Box>

      <UserForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleFormSubmit}
        user={currentUser}
        amcId={currentUserAMC}
        loggedInUserIsAdmin={currentUserIsAdmin}
      />

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this user? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
